// src/components/nav/DfsContestBottomNav.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { buildDfsContestSectionHref, getDfsContestMobileView, type DfsContestMobileView } from '@/utils/dfsContestNavigation';

const NavItem: React.FC<{
    to: string;
    label: string;
    active: boolean;
    children: React.ReactNode;
}> = ({ to, label, active, children }) => {
    return (
        <Link
            to={to}
            aria-current={active ? 'page' : undefined}
            className={`
                flex flex-col items-center justify-center w-full pt-1.5 pb-1 mx-0.5 rounded-lg
                transition-colors duration-150 ease-in-out
                active:opacity-70
                ${active ? 'bg-accent-yellow text-primary font-bold' : 'text-gray-400'}
            `}
        >
            {children}
            <span className="text-badge font-medium mt-1">{label}</span>
        </Link>
    );
};

interface DfsContestBottomNavProps {
  contestIdOrAlias: string | number | null | undefined;
}

export const DfsContestBottomNav: React.FC<DfsContestBottomNavProps> = ({ contestIdOrAlias }) => {
    const location = useLocation();
    if (!contestIdOrAlias) return null;

    const activeSection = getDfsContestMobileView(location.hash);
    const sectionHref = (view: DfsContestMobileView) => buildDfsContestSectionHref(contestIdOrAlias, view);

    return (
        <nav className="fixed bottom-0 left-0 right-0 bg-primary border-t border-gray-700/50 shadow-lg z-40 pb-[env(safe-area-inset-bottom)]">
            <div className="mx-auto flex h-[3.25rem] max-w-md items-center justify-around">
                <NavItem to={sectionHref('lineup')} label="Lineup" active={activeSection === 'lineup'}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                    </svg>
                </NavItem>

                <NavItem to={sectionHref('leaderboard')} label="Leaderboard" active={activeSection === 'leaderboard'}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                </NavItem>

                <NavItem to={sectionHref('matches')} label="Matches" active={activeSection === 'matches'}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                </NavItem>

                <NavItem to={sectionHref('players')} label="Players" active={activeSection === 'players'}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                    </svg>
                </NavItem>
            </div>
        </nav>
    );
};

export default DfsContestBottomNav;
